import style from "./AddCartCountModal.module.css";
import NiceModal from "@ebay/nice-modal-react";
import ModalBase from "modals/base/ModalBase";
import PaddingWrapper from "layouts/wrapper/PaddingWrapper";
import VerticalFlex from "layouts/flex/VerticalFlex";
import FlexChild from "layouts/flex/FlexChild";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { getCurrentLanguageCode, validateInputs } from "shared/utils/Utils";
import { useDispatch } from "react-redux";
import { requester, storeRequester } from "App";
import { CartReducer } from "shared/redux/reducers/shopping/CartReducer";
import ButtonEclipse from "components/buttons/ButtonEclipse";
import InputNumber from "components/inputs/InputNumber";
import { AuthContext } from "providers/AuthProvider";
import { ToastContainer, toast } from "react-toastify";
import SquareWrapper from "layouts/wrapper/SquareWrapper";
import P from "components/P";
import { Cookie, getCookie } from "shared/StoreRequester";
import { RegionContext } from "providers/RegionProvider";

const AddCartCountModal = NiceModal.create((props) => {
  const { t } = useTranslation();
  const modal = useRef();
  const inputs = useRef([]);
  const { userName } = useContext(AuthContext);
  const { regionId } = useContext(RegionContext);
  const { product } = props;

  const [cartItems, setCartItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // 장바구니 정보 가져오기
  useEffect(() => {
    const fetchCart = async () => {
      const { cart } = await storeRequester.getMyCart(regionId);
      setCartItems(cart?.items || []);
    };
    fetchCart();
  }, [regionId]);

  // 입력 변경 핸들러
  const handleInputChange = (variantId, index) => {
    setSelectedItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        item => item.variantId === variantId
      );
      if (existingItemIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          index: index,
        };
        return updatedItems;
      }
      return [...prevItems, { variantId, index }];
    });
  };

  const calculateRemainingInventory = (variantId) => {
    const cartItem = cartItems.find(item => item.variant_id === variantId);
    const cartQuantity = cartItem?.quantity || 0;

    const variant = product.variants
      ? product.variants.find(v => v.id === variantId)
      : product;

    const remainingQuantity = variant.inventory_quantity - cartQuantity;
    return remainingQuantity > 0 ? remainingQuantity : 0;
  };

  // 재고 체크 함수
  const checkInventoryLimit = (variantId, quantity) => {
    const cartItem = cartItems.find(item => item.variant_id === variantId);
    const cartQuantity = cartItem?.quantity || 0;

    const variant = product.variants
      ? product.variants.find(v => v.id === variantId)
      : product;

    return (cartQuantity + quantity) <= variant.inventory_quantity;
  };

  // 장바구니 추가 핸들러
  const onAddCartClick = async () => {
    const medusaCartId = getCookie(Cookie.Cart);
    if (!userName || !medusaCartId) {
      NiceModal.show("memberSignIn");
      return;
    }

    const validationResult = await validateInputs(inputs.current);
    if (!validationResult.isValid) {
      toast.error(t("pleaseCheckTheQuantity"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      return;
    }

    try {
      // variants 존재 여부로 분기
      if (product.variants?.length) {
        const itemsToAdd = selectedItems.map(item => {
          const quantity = inputs.current[item.index].getValue();
          if (!checkInventoryLimit(item.variantId, quantity)) {
            throw new Error(`재고를 확인해주세요.`);
          }
          return {
            variant_id: item.variantId,
            quantity,
            free: false,
          };
        });

        await storeRequester.addItem(medusaCartId, itemsToAdd);
      } else {
        const quantity = inputs.current[0].getValue();
        if (!checkInventoryLimit(product.id, quantity)) {
          throw new Error(`재고를 확인해주세요.`);
        }

        await storeRequester.addItem(medusaCartId, {
          variant_id: product.id,
          quantity,
          free: false,
        });
      }

      toast.success(t("putInAShoppingCart"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
      modal.current?.close()
    } catch (error) {
      toast.error(error.message || t("unexpected Error"), {
        autoClose: 1000,
        position: toast.POSITION.BOTTOM_CENTER,
      });
    }
  };

  return (
    <ModalBase
      ref={modal}
      width="min(80%, 600px)"
      height="fit-content"
      withHeader={true}
      withFooter={false}
      withCloseButton={false}
      clickOutsideToClose={true}
      title="장바구니 담기"
    >
      <PaddingWrapper padding={"10px 20px"}>
        {product.variants?.length ? (
          <>
            <P>{product.title}</P>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {product.variants.map((variant, index) => (
                <VerticalFlex
                  key={variant.id}
                  gap={10}
                  margin={"10px"}
                  flexBasis={"25%"}
                  maxWidth={"21%"}
                >
                  <FlexChild alignItems={"flex-start"}>
                    <P>{variant.title}</P>
                  </FlexChild>
                  <FlexChild>
                    <SquareWrapper>
                      <img
                        src={variant.thumbnail}
                        style={{
                          width: "100%",
                          borderRadius: "5px",
                          opacity: (calculateRemainingInventory(variant.id) === 0 || variant.sold) ? 0.2 : 1,
                        }}
                        className={style.image}
                      />
                      {(calculateRemainingInventory(variant.id) === 0 || variant.sold) && (
                        <div
                          style={{
                            position: "absolute",
                            top: "45%",
                            left: "10px",
                            zIndex: 6,
                          }}
                        >
                          <P size={20} weight={700} color={"#ca0000"}>
                            SOLD OUT
                          </P>
                        </div>
                      )}
                    </SquareWrapper>
                  </FlexChild>
                  <FlexChild>
                    <InputNumber
                      onChange={() => handleInputChange(variant.id, index)}
                      ref={(el) => (inputs.current[index] = el)}
                      min={0}
                      max={variant.sold ? 0 : calculateRemainingInventory(variant.id)}
                      required={true}
                      height={50}
                    />
                  </FlexChild>
                </VerticalFlex>
              ))}
            </div>
          </>
        ) : (
          <VerticalFlex gap={10}>
            <FlexChild alignItems={"flex-start"}>
              <P>{product.title}</P>
              <P>{product.description}</P>
            </FlexChild>
            <FlexChild>
              <SquareWrapper>
                <img
                  src={product.thumbnail}
                  style={{
                    width: "100%",
                    borderRadius: "5px",
                    opacity: (calculateRemainingInventory(product.id) === 0 || product.sold) ? 0.2 : 1,
                  }}
                  className={style.image}
                />
                {(calculateRemainingInventory(product.id) === 0 || product.sold) && (
                  <div
                    style={{
                      position: "absolute",
                      top: "45%",
                      left: "40%",
                      zIndex: 6,
                    }}
                  >
                    <P size={20} weight={700} color={"#ca0000"}>
                      SOLD OUT
                    </P>
                  </div>
                )}
              </SquareWrapper>
            </FlexChild>
            <FlexChild>
              <InputNumber
                ref={(el) => (inputs.current[0] = el)}
                min={0}
                max={product.sold ? 0 : calculateRemainingInventory(product.id)}
                required={true}
                height={50}
              />
            </FlexChild>
          </VerticalFlex>
        )}
        <FlexChild>
          <ButtonEclipse
            text={t("addToBasket")}
            onClick={onAddCartClick}
            borderRadius={5}
            height={44}
            backgroundColor={"var(--main-color)"}
            fontWeight={"bold"}
          />
        </FlexChild>
      </PaddingWrapper>
    </ModalBase>
  );
});

export default AddCartCountModal;